import * as React from 'react';
import PropTypes from 'prop-types';
import { PieChart } from '@mui/x-charts/PieChart';
import { useDrawingArea } from '@mui/x-charts/hooks';
import { styled } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import LinearProgress, { linearProgressClasses } from '@mui/material/LinearProgress';

import { DirectionsCar, EvStation, Garage, LocationOn, Power } from '@mui/icons-material';

const data = [
  { label: '🚗 Driving', value: 45000, color: 'hsl(68, 76.10%, 54.10%)' },
  { label: '🌎 Outside', value: 35000, color: 'hsl(257, 93.30%, 47.10%)' },
  { label: '🏡 Home Parked', value: 10000, color: 'hsl(219, 41.10%, 68.00%)', },
  { label: '🔌 Home Plugged', value: 5000, color: 'hsl(219, 68.70%, 41.40%)', },
  { label: '⚡ Home Charging', value: 5000, color: 'hsl(142, 58.90%, 50.40%)', },
];

const CatStatuses = [
  {
    name: '🚗 Driving',
    value: 45,
    flag: <DirectionsCar />,
    color: 'hsl(68, 76.10%, 54.10%)',
  },
  {
    name: '🌎 Outside',
    value: 35,
    flag: <LocationOn />,
    color: 'hsl(257, 93.30%, 47.10%)',
  },
  {
    name: '🏡 Home Parked',
    value: 10,
    flag: <Garage />,
    color: 'hsl(219, 41.10%, 68.00%)',
  },
  {
    name: '🔌 Home Plugged',
    value: 5,
    flag: <Power />,
    color: 'hsl(219, 68.70%, 41.40%)',
  },
  {
    name: '⚡ Home Charging',
    value: 5,
    flag: <EvStation />,
    color: 'hsl(142, 58.90%, 50.40%)',
  },
];

const StyledText = styled('text', {
  shouldForwardProp: (prop) => prop !== 'variant',
})(({ theme }) => ({
  textAnchor: 'middle',
  dominantBaseline: 'central',
  fill: (theme.vars || theme).palette.text.secondary,
  variants: [
    {
      props: {
        variant: 'primary',
      },
      style: {
        fontSize: theme.typography.h5.fontSize,
      },
    },
    {
      props: ({ variant }) => variant !== 'primary',
      style: {
        fontSize: theme.typography.body2.fontSize,
      },
    },
    {
      props: {
        variant: 'primary',
      },
      style: {
        fontWeight: theme.typography.h5.fontWeight,
      },
    },
    {
      props: ({ variant }) => variant !== 'primary',
      style: {
        fontWeight: theme.typography.body2.fontWeight,
      },
    },
  ],
}));

function PieCenterLabel({ primaryText, secondaryText }) {
  const { width, height, left, top } = useDrawingArea();
  const primaryY = top + height / 2 - 10;
  const secondaryY = primaryY + 24;

  return (
    <React.Fragment>
      <StyledText variant="primary" x={left + width / 2} y={primaryY}>
        {primaryText}
      </StyledText>
      <StyledText variant="secondary" x={left + width / 2} y={secondaryY}>
        {secondaryText}
      </StyledText>
    </React.Fragment>
  );
}

PieCenterLabel.propTypes = {
  primaryText: PropTypes.string.isRequired,
  secondaryText: PropTypes.string.isRequired,
};

const colors = [
  'hsl(220, 20%, 65%)',
  'hsl(220, 20%, 42%)',
  'hsl(220, 20%, 35%)',
  'hsl(220, 20%, 25%)',
];

export default function CarStatus() {
  return (
    <Card
      variant="outlined"
      sx={{ display: 'flex', flexDirection: 'column', gap: '8px', flexGrow: 1 }}
    >
      <CardContent>
        <Typography component="h2" variant="subtitle2">
          Car Statuses
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <PieChart
            colors={colors}
            margin={{
              left: 80,
              right: 80,
              top: 80,
              bottom: 80,
            }}
            series={[
              {
                data,
                innerRadius: 75,
                outerRadius: 100,
                paddingAngle: 0,
                highlightScope: { faded: 'global', highlighted: 'item' },
              },
            ]}
            height={260}
            width={260}
            slotProps={{
              legend: { hidden: true },
            }}
          >
            <PieCenterLabel primaryText="4,500 EVs" secondaryText="Total" />
          </PieChart>
        </Box>
        {CatStatuses.map((carInfo, index) => (
          <Stack
            key={index}
            direction="row"
            sx={{ alignItems: 'center', gap: 2, pb: 2 }}
          >
            {carInfo.flag}
            <Stack sx={{ gap: 1, flexGrow: 1 }}>
              <Stack
                direction="row"
                sx={{
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  gap: 2,
                }}
              >
                <Typography variant="body2" sx={{ fontWeight: '500' }}>
                  {carInfo.name}
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  {carInfo.value}%
                </Typography>
              </Stack>
              <LinearProgress
                variant="determinate"
                aria-label="Number of users by country"
                value={carInfo.value}
                sx={{
                  [`& .${linearProgressClasses.bar}`]: {
                    backgroundColor: carInfo.color,
                  },
                }}
              />
            </Stack>
          </Stack>
        ))}
      </CardContent>
    </Card>
  );
}
